/**
 * How this build signs people in (STE-1012), and the rules the Auth0 sign-in
 * follows. Kept apart from the React wiring so each rule can be checked
 * without a browser.
 */

export type Provider = 'mock' | 'cognito' | 'auth0'

type Env = Record<string, string | boolean | undefined>

/**
 * Mock mode is mock mode, whatever else is set. Otherwise Auth0 only when the
 * build says so; anything else keeps the Cognito screens.
 */
export function providerFrom(env: Env): Provider {
  if (env.VITE_USE_MOCK !== 'false') return 'mock'
  return env.VITE_AUTH_PROVIDER === 'auth0' ? 'auth0' : 'cognito'
}

export interface Auth0Settings { domain: string; clientId: string; audience: string }

/** All three, or none: an Auth0 build with a value missing has nothing to sign in with. */
export function auth0SettingsFrom(env: Env): Auth0Settings | null {
  const domain = String(env.VITE_AUTH0_DOMAIN ?? '').trim()
  const clientId = String(env.VITE_AUTH0_CLIENT_ID ?? '').trim()
  const audience = String(env.VITE_AUTH0_AUDIENCE ?? '').trim()
  return domain && clientId && audience ? { domain, clientId, audience } : null
}

/**
 * What the SDK is given.
 *
 * The session lives in memory only. On a reload the SDK renews it silently
 * through the tenant's custom domain, which shares the site with the app, so
 * Auth0's session cookie is first party and nothing is written to browser
 * storage (FR-AU21).
 */
export function auth0Options(s: Auth0Settings, origin: string) {
  return {
    domain: s.domain,
    clientId: s.clientId,
    authorizationParams: { redirect_uri: origin, audience: s.audience },
    cacheLocation: 'memory' as const,
    useRefreshTokens: true,
    useRefreshTokensFallback: true,
  }
}

export const NO_COMPANY = 'This login is not set up for Stello. Ask your HR team.'
export const COULD_NOT_REACH = 'Signing in could not be finished. Check your connection and try again.'
/** Our Action's words when somebody at a single sign-on company tries a password (STE-1022). */
export const USE_COMPANY_LOGIN = 'Your company logs in with its own single sign-on. Log in with your company login instead of a password.'

/**
 * The plain words for a sign-in that did not work.
 *
 * Refused by our own API for want of a company, or denied by our Action on
 * Auth0's page for the same reason: both say the same thing, because to the
 * person they are the same thing.
 */
export function signInProblem(err: unknown): string {
  const e = (err ?? {}) as { status?: number; message?: string; error?: string; error_description?: string }
  if (e.status === 401 && /not attached to a company/i.test(e.message ?? '')) return NO_COMPANY
  if (e.error === 'access_denied' && (e.error_description ?? '') === NO_COMPANY) return NO_COMPANY
  if (e.error === 'access_denied' && (e.error_description ?? '') === USE_COMPANY_LOGIN) return USE_COMPANY_LOGIN
  return COULD_NOT_REACH
}
